// New-design rollout ("new view") — copy + config for the opt-in surfaces.

// Feature bullets shown in the "new view" welcome modals (NewDesignWelcomeModal).
// Same for both prompt stages. Each: { title, text }.
export const NEW_DESIGN_WELCOME_FEATURES = [
  { title: 'Consolidated Inventory and Batches', text: 'Manage your cards in a single place.' },
  { title: 'Editing from both views', text: 'Now you can edit cards directly from inventory.' },
  { title: 'Cleaner look & feel', text: 'Everything you need, now simpler.' },
]

// Two-stage welcome prompt. Stage 1 shows first; if the user picks "Maybe later"
// the stage-2 (follow-up) prompt appears after a delay. Only title/description
// differ — bullets, image and buttons are identical. `descriptionExtra` renders
// on desktop only (dropped on mobile to keep the card short).
export const NEW_DESIGN_PROMPTS = {
  first: {
    title: 'Welcome to Inventory Beta',
    description: "We've unified Inventory and Batches to help you manage your cards more efficiently.",
    descriptionExtra: '',
  },
  second: {
    title: 'Ready to try the new and improved inventory?',
    description: "We've unified Inventory and Batches to help you manage your cards more efficiently.",
    descriptionExtra: '',
  },
}

// Delay before the stage-2 follow-up prompt appears after the first
// "Maybe later". TEST value — 10s; raise for production.
export const NEW_DESIGN_SECOND_PROMPT_DELAY_MS = 10_000

// (The top-of-Inventory promo banner was removed 2026-08-25.)
//
// Sidebar invite to the new design (Figma 27075:431176) — classic-design users
// only, expanded sidebar only.
export const NEW_DESIGN_SIDEBAR_PROMO = Object.freeze({
  title: 'Inventory Beta is here!',
  description: 'Our improved inventory unifies inventory and batches to help you manage cards more efficiently.',
  cta: 'Try it out',
})

// First-run guided tour shown once a user lands on the new-design Inventory.
// Copy only — targets + side effects (open panel, open batch) live in
// NewDesignTour.vue. Order = tour order.
export const NEW_DESIGN_TOUR = [
  { description: 'Switch between the new and classic view at any time.' },
  { description: 'Browse your batches and open any of them right inside Inventory.' },
  { description: 'Now you can edit cards directly from inventory.' },
]

// The Inventory design switch (Figma 27075:434023 / 27075:434240). One button
// whose copy + weight depend on which design is running: leaving the new design
// is a quiet text link, coming back to it is an invitation.
export const INVENTORY_DESIGN_SWITCH = Object.freeze({
  toLegacy: {
    label: 'Switch to legacy inventory',
    icon: 'heroicons:arrows-up-down-16-solid',
    variant: 'link',
    // Mobile confirm (Figma 27079:494998) — switching reloads and rebuilds the
    // inventory cache, which is slow enough on a phone to warrant a heads-up.
    confirmTitle: 'Switch to legacy inventory',
    confirmLabel: 'Switch View',
  },
  toBeta: {
    label: 'Try Inventory Beta',
    icon: 'heroicons:sparkles-16-solid',
    variant: 'secondary',
    // Figma 27079:495525
    confirmTitle: 'Try Inventory Beta?',
    confirmLabel: 'Try now',
  },
})

// Both directions share the wait, so they share the sentence.
export const INVENTORY_DESIGN_SWITCH_DESCRIPTION =
  'This may take a moment to load and update your active inventory cache.'

// ─── Unified card grid (Inventory ≡ batch-in-inventory) ──────────────────────
// New design collapses the two card tables into one: same status tabs, same
// columns, same editing rules. `/batches/:id` (classic) is untouched.

// Status tabs above the grid. Mirrors the 5-state `CARD_STATUS_META` model
// (+ "All"), so the tab a user picks is the status the grid shows in the
// Status column. Serialized to the API as `status=<key>` — see
// `STATUS_TAB_FILTERS` in constants/filters.js for the label → key map.
export const UNIFIED_STATUS_TAB_OPTIONS = [
  'All',
  'Draft',
  'Ready',
  'Listed',
  'Sold Out',
  'Out of Stock',
]

// Canonical column order + labels for the unified grid: the union of the batch
// and inventory column sets, in the order `unified-columns.js` declares them.
// `state` / `select` / `images` are omitted on purpose — they're structural and
// never user-toggleable.
//
// EVERY column ships visible. A user's own hidden columns are not lost: saved
// display-column lists are merged over these defaults (`mergeColumnOptions` in
// helpers/inventory.js), so an unchecked column stays unchecked and only genuinely
// NEW columns arrive checked.
export const UNIFIED_COLUMN_OPTIONS = [
  { field: 'title',             name: 'Product',            eBay: true,   shopify: true,   checked: true },
  { field: 'sku',               name: 'SKU',                eBay: true,   shopify: true,   checked: true },
  { field: 'database_match',    name: 'Database Match',     eBay: true,   shopify: true,   checked: true },
  { field: 'actions',           name: 'Actions',            eBay: true,   shopify: true,   checked: true },
  { field: 'quantity',          name: 'On Hand',            eBay: true,   shopify: true,   checked: true },
  { field: 'availableQuantity', name: 'For Sale',           eBay: true,   shopify: true,   checked: true },
  { field: 'listings',          name: 'Listings',           eBay: true,   shopify: true,   checked: true },
  { field: 'start_price',       name: 'Sale Price',         eBay: true,   shopify: true,   checked: true },
  { field: 'sport',             name: 'Category',           eBay: true,   shopify: true,   checked: true },
  { field: 'status',            name: 'Status',             eBay: true,   shopify: true,   checked: true },
  { field: 'batch_id',          name: 'Batch ID',           eBay: true,   shopify: true,   checked: true },
  { field: 'year',              name: 'Year',               eBay: true,   shopify: true,   checked: true },
  { field: 'brand',             name: 'Brand',              eBay: true,   shopify: true,   checked: true },
  { field: 'weight_oz',         name: 'Weight (oz)',        eBay: false,  shopify: false, checked: true },
  { field: 'set',               name: 'Set',                eBay: true,   shopify: true,   checked: true },
  { field: 'player',            name: 'Name',               eBay: true,   shopify: true,   checked: true },
  { field: 'card_number',       name: 'Card No.',           eBay: true,   shopify: true,   checked: true },
  { field: 'subset',            name: 'Subset/Parallel',    eBay: true,   shopify: true,   checked: true },
  { field: 'attributes',        name: 'Attributes',         eBay: true,   shopify: true,   checked: true },
  { field: 'team',              name: 'Team',               eBay: true,   shopify: true,   checked: true },
  { field: 'variation',         name: 'Variation',          eBay: true,   shopify: true,   checked: true },
  { field: 'graded',            name: 'Graded',             eBay: true,   shopify: true,   checked: true },
  { field: 'grader',            name: 'Grader',             eBay: true,   shopify: true,   checked: true },
  { field: 'condition',         name: 'Condition',          eBay: true,   shopify: true,   checked: true },
  { field: 'cert_no',           name: 'Cert No. ',          eBay: true,   shopify: true,   checked: true },
  { field: 'grade_number',      name: 'Grade',              eBay: true,   shopify: true,   checked: true },
  { field: 'purchase_date',     name: 'Purchase Date',      eBay: false,  shopify: false, checked: true },
  { field: 'purchase_price',    name: 'Purchase Price',     eBay: false,  shopify: false, checked: true },
  { field: 'note',              name: 'Note',               eBay: false,  shopify: false, checked: true },
  { field: 'cabinet',           name: 'Cabinet',            eBay: false,  shopify: false, checked: true },
  { field: 'shelf',             name: 'Shelf',              eBay: false,  shopify: false, checked: true },
  { field: 'box',               name: 'Box',                eBay: false,  shopify: false, checked: true },
  { field: 'row',               name: 'Row',                eBay: false,  shopify: false, checked: true },
  { field: 'section',           name: 'Section',            eBay: false,  shopify: false, checked: true },
  { field: 'description',       name: 'Description',        eBay: true,   shopify: true,   checked: true },
]

// Legacy inventory option fields whose `field` never matched a real column
// ('name' had no column at all, so Product was permanently visible). Saved
// display-column lists are rewritten through this map on load so a user's
// visibility choices survive the move to the unified fields.
export const UNIFIED_COLUMN_ALIASES = {
  name: 'title',
  inventoryStatus: 'status',
  sale_price: 'start_price',
  image: 'images',
}
